import { type GitlabProvisioningCoverageState } from 'network/httpClients/GitlabClient';
import {
  type GitlabV2GroupInstallPartition,
  type GitlabV2InstallSelection,
  type GitlabV2ManageDeltas,
  type GitlabV2ScopePaths,
  type GitlabV2SelectionState,
} from './types';

/** The top-level segment of a group/project path (its owning top-level group). */
const topLevelSegment = (path: string): string => path.split('/')[0];

/**
 * Whether a Select scope is already covered by a Qodo hook (own or an ancestor
 * group's). Positive allowlist: a missing/unexpected value fails safe to "not
 * covered" (selectable), so bad data can't lock the whole tree.
 * @param coverageState - The scope's coverage state (may be missing at runtime).
 * @returns True only for `own_hook` or `covered_by_group`.
 */
export const isScopeCovered = (
  coverageState: GitlabProvisioningCoverageState | undefined,
): boolean =>
  coverageState === 'own_hook' || coverageState === 'covered_by_group';

/**
 * Whether `path` is `ancestorPath` itself or a descendant of it (by fullPath
 * segments — segment-aware so `ab` is not a descendant of `a`).
 * @param path - The candidate scope's fullPath.
 * @param ancestorPath - The potential ancestor's fullPath.
 * @returns True when path === ancestorPath or path is nested under it.
 */
export const isDescendantOrSelf = (
  path: string,
  ancestorPath: string,
): boolean => path === ancestorPath || path.startsWith(`${ancestorPath}/`);

/**
 * Whether a scope is covered by any selected group (the group itself or an
 * ancestor) — a group-level webhook covers the whole subtree.
 * @param fullPath - The scope's fullPath.
 * @param selectedGroupPaths - fullPaths of the currently selected groups.
 * @returns True when some selected group covers this scope.
 */
export const isCoveredBySelectedGroup = (
  fullPath: string,
  selectedGroupPaths: string[],
): boolean =>
  selectedGroupPaths.some((groupPath) =>
    isDescendantOrSelf(fullPath, groupPath),
  );

/**
 * Whether a group is covered by a *different* selected group (a strict ancestor).
 * Excludes the group's own path, so a selected group is never "covered" by its own
 * selection — it stays checked-and-unlockable, while its subgroups render read-only.
 * @param fullPath - The group's fullPath.
 * @param selectedGroupPaths - fullPaths of the currently selected groups.
 * @returns True when a strict ancestor of `fullPath` is selected.
 */
export const isCoveredBySelectedAncestorGroup = (
  fullPath: string,
  selectedGroupPaths: string[],
): boolean =>
  selectedGroupPaths.some(
    (groupPath) =>
      groupPath !== fullPath && isDescendantOrSelf(fullPath, groupPath),
  );

/**
 * Drop selected groups already covered by another selected ancestor — selecting
 * a parent installs one group webhook covering the subtree, so nested selections
 * are redundant.
 * @param selectedGroupPaths - fullPaths of the selected groups.
 * @returns The minimal set of top-most selected paths.
 */
export const getEffectiveGroupPaths = (
  selectedGroupPaths: string[],
): string[] =>
  selectedGroupPaths.filter(
    (path) =>
      !selectedGroupPaths.some(
        (other) => other !== path && isDescendantOrSelf(path, other),
      ),
  );

/**
 * Compute the install payload from the tree selection. A selected group becomes a
 * group webhook only when its tier supports one (`canGroupHook`) — Premium+ groups
 * install one group webhook per effective (non-covered) selected group; every selected
 * repo not already covered by such a group becomes a project webhook. On gitlab.com
 * multi-group, tier is per top-level group, so `canGroupHook` is resolved per id.
 * Filtering non-group-hook groups here is the fail-safe: even a stale selection can
 * never emit a group hook the backend can't honor (a Free group is drill-only in the
 * UI, so it never enters the selection in the first place).
 * @param state - The current selection state.
 * @param groupPathsById - Map of loaded group id → fullPath (to resolve coverage).
 * @param canGroupHook - Whether a given group id's tier supports a group webhook.
 * @param projectPathsById - Map of loaded project id → pathWithNamespace (to drop repos covered by a selected group).
 * @returns The `{ groupIds, projectIds }` payload for the install request.
 */
export const buildInstallSelection = (
  state: GitlabV2SelectionState,
  groupPathsById: Map<number, string>,
  canGroupHook: (groupId: number) => boolean,
  projectPathsById: Map<number, string>,
): GitlabV2InstallSelection => {
  const selectedGroupIds = Array.from(state.selectedGroupIds).filter(
    canGroupHook,
  );
  const selectedPaths = selectedGroupIds
    .map((id) => groupPathsById.get(id))
    .filter((path): path is string => path !== undefined);
  const effectivePaths = new Set(getEffectiveGroupPaths(selectedPaths));

  const groupIds = selectedGroupIds.filter((id) => {
    const path = groupPathsById.get(id);

    return path !== undefined && effectivePaths.has(path);
  });

  // A repo covered by a selected group needs no project hook (the group hook
  // covers it). An unresolved path is kept (defensive — shouldn't happen once
  // every loaded item carries its path).
  const projectIds = Array.from(state.selectedProjectIds).filter((id) => {
    const path = projectPathsById.get(id);

    return path === undefined || !isCoveredBySelectedGroup(path, selectedPaths);
  });

  return { groupIds, projectIds };
};

/**
 * Whether an install selection is empty (nothing to install → Next disabled).
 * @param selection - The computed install selection.
 * @returns True when no groups and no projects are selected.
 */
export const isSelectionEmpty = (
  selection: GitlabV2InstallSelection,
): boolean =>
  selection.groupIds.length === 0 && selection.projectIds.length === 0;

/** Ids in `set` that are not in `other`. */
const setDifference = (
  set: ReadonlySet<number>,
  other: ReadonlySet<number>,
): number[] => Array.from(set).filter((id) => !other.has(id));

/**
 * Compute the `/manage` add/remove deltas from the baseline (pre-ticked
 * `own_hook` scopes) vs. the current selection. Group mode (Premium+) diffs
 * whole groups; repo mode (Free/CE) diffs individual projects. `covered_by_group`
 * scopes are rendered read-only and never enter the selection, so they can't
 * appear in either delta. On the add side, nested groups already covered by
 * another newly-added ancestor are dropped (one group hook covers the subtree).
 * @param baseline - The pre-ticked removable scopes captured on list load.
 * @param selection - The current (toggled) selection state.
 * @param groupPathsById - Map of loaded group id → fullPath (resolves add-side coverage).
 * @param groupWebhooksSupported - Whether group-level webhooks are available.
 * @param projectPathsById - Map of loaded project id → pathWithNamespace (drops added repos covered by a selected group).
 * @returns The `{ add, remove }` deltas for the manage request.
 */
export const buildManageDeltas = (
  baseline: GitlabV2SelectionState,
  selection: GitlabV2SelectionState,
  groupPathsById: Map<number, string>,
  groupWebhooksSupported: boolean,
  projectPathsById: Map<number, string>,
): GitlabV2ManageDeltas => {
  if (!groupWebhooksSupported) {
    return {
      add: {
        groupIds: [],
        projectIds: setDifference(
          selection.selectedProjectIds,
          baseline.selectedProjectIds,
        ),
      },
      remove: {
        groupIds: [],
        projectIds: setDifference(
          baseline.selectedProjectIds,
          selection.selectedProjectIds,
        ),
      },
    };
  }

  const addedGroupIds = setDifference(
    selection.selectedGroupIds,
    baseline.selectedGroupIds,
  );
  const removedGroupIds = setDifference(
    baseline.selectedGroupIds,
    selection.selectedGroupIds,
  );

  const addedPaths = addedGroupIds
    .map((id) => groupPathsById.get(id))
    .filter((path): path is string => path !== undefined);
  const effectiveAddPaths = new Set(getEffectiveGroupPaths(addedPaths));
  const effectiveAddGroupIds = addedGroupIds.filter((id) => {
    const path = groupPathsById.get(id);

    return path !== undefined && effectiveAddPaths.has(path);
  });

  // fullPaths of every currently selected group — an added repo under one of them
  // needs no project hook (the group hook covers it), so drop it from the add.
  const selectedGroupPaths = Array.from(selection.selectedGroupIds)
    .map((id) => groupPathsById.get(id))
    .filter((path): path is string => path !== undefined);
  const addedProjectIds = setDifference(
    selection.selectedProjectIds,
    baseline.selectedProjectIds,
  ).filter((id) => {
    const path = projectPathsById.get(id);

    return (
      path === undefined || !isCoveredBySelectedGroup(path, selectedGroupPaths)
    );
  });
  const removedProjectIds = setDifference(
    baseline.selectedProjectIds,
    selection.selectedProjectIds,
  );

  return {
    add: { groupIds: effectiveAddGroupIds, projectIds: addedProjectIds },
    remove: { groupIds: removedGroupIds, projectIds: removedProjectIds },
  };
};

/**
 * Whether the manage deltas are empty (no add/remove → Save disabled, no-op).
 * @param deltas - The computed manage deltas.
 * @returns True when both add and remove are empty.
 */
export const isManageDeltasEmpty = (deltas: GitlabV2ManageDeltas): boolean =>
  deltas.add.groupIds.length === 0 &&
  deltas.add.projectIds.length === 0 &&
  deltas.remove.groupIds.length === 0 &&
  deltas.remove.projectIds.length === 0;

/**
 * Split a cloud multi-group install selection into one partition per top-level group,
 * so Install can provision one installation per group then `/manage` that group's scopes.
 * Each selected group/project is attributed by its BE-stamped `topLevelGroupId` (tracked
 * as items load) — correct even for a searched-deep item whose top-level group was never
 * loaded at the root. The top-level group's path (for display) is the first segment of a
 * member's fullPath. A selection with no known top-level group id is skipped (defensive;
 * shouldn't happen once every item carries `topLevelGroupId`).
 * @param selection - The computed install selection ({ groupIds, projectIds }).
 * @param scopePaths - Loaded id→path and id→topLevelGroupId maps for groups and projects.
 * @returns One partition per top-level group with a selection, in insertion order.
 */
export const partitionSelectionByTopLevelGroup = (
  selection: GitlabV2InstallSelection,
  scopePaths: GitlabV2ScopePaths,
): GitlabV2GroupInstallPartition[] => {
  const partitions = new Map<number, GitlabV2GroupInstallPartition>();

  // The top-level group's path (for display) is the first segment of any member's
  // fullPath — same for a whole-group or a deep member selection.
  const partitionFor = (
    topLevelGroupId: number,
    memberPath: string | undefined,
  ): GitlabV2GroupInstallPartition => {
    let partition = partitions.get(topLevelGroupId);

    if (!partition) {
      partition = {
        topLevelGroupId,
        groupPath: memberPath !== undefined ? topLevelSegment(memberPath) : '',
        add: { groupIds: [], projectIds: [] },
      };
      partitions.set(topLevelGroupId, partition);
    } else if (partition.groupPath === '' && memberPath !== undefined) {
      partition.groupPath = topLevelSegment(memberPath);
    }

    return partition;
  };

  selection.groupIds.forEach((id) => {
    const topLevelGroupId = scopePaths.topLevelGroupIdByGroupId.get(id);

    if (topLevelGroupId === undefined) return;

    partitionFor(
      topLevelGroupId,
      scopePaths.groupPathsById.get(id),
    ).add.groupIds.push(id);
  });

  selection.projectIds.forEach((id) => {
    const topLevelGroupId = scopePaths.topLevelGroupIdByProjectId.get(id);

    if (topLevelGroupId === undefined) return;

    partitionFor(
      topLevelGroupId,
      scopePaths.projectPathsById.get(id),
    ).add.projectIds.push(id);
  });

  return Array.from(partitions.values());
};
